import { Space } from 'antd';
import moment from 'moment';
import { useContext, useMemo } from 'react';

import { DATE_FORMATS, VISITS_FILTER } from '@app/constants';
import { useMemoizeObject } from '@app/hooks';
import {
  CHF_DATE_END,
  CHF_DATE_START,
} from '@app/pages/Clients/ClientsList/constants/filterKeysAndIds';
import { ClientFiltersContext } from '@app/providers';

import { defineFastDatesFilter } from '@pages/Clients/ClientsList/utils';

import {
  CUSTOM_PERIOD,
  LAST_N_DAYS,
  PERIODS,
} from '@pages/Clients/ClientsList/constants/periods';

import { DatesTagContainer } from '@pages/Clients/ClientsList/components/Filters';

import { initialFiltersConfig } from '@pages/Clients/ClientsList/data/initialFiltersConfig';

const filtersArea = VISITS_FILTER;

export const VisitsPeriodLabel = ({
  filterGroupIndex,
}: {
  filterGroupIndex: number;
}) => {
  const { serverFilters } = useContext(ClientFiltersContext);

  const memoizedServerFilters =
    useMemoizeObject(serverFilters) || initialFiltersConfig;

  const periodTags = useMemo(() => {
    const filter =
      memoizedServerFilters.VisitsFilter?.[filterGroupIndex]?.Values?.[0];
    const dateStart = filter?.DateStart || null;
    const dateEnd = filter?.DateEnd || null;

    const period = defineFastDatesFilter({
      startDate: dateStart || '',
      endDate: dateEnd || '',
      today: moment().format(DATE_FORMATS.ISO_FORMAT_NO_TIMEZONE),
    });

    if (period === LAST_N_DAYS) {
      const calculateLastNDays = (
        startDate: string | null,
        endDate: string | null
      ) => {
        const start = moment(startDate);
        const end = moment(endDate);
        return end.diff(start, 'days');
      };

      const lastNDays = calculateLastNDays(dateStart, dateEnd);

      return (
        <DatesTagContainer
          filtersArea={filtersArea}
          filterGroupIndex={filterGroupIndex}
          label={`Последних дней: ${lastNDays}`}
          valueKey={CHF_DATE_START}
        />
      );
    } else if (period === CUSTOM_PERIOD && (dateStart || dateEnd)) {
      const handleStartDate = new Date(String(dateStart)).toLocaleDateString();
      const handleEndDate = new Date(String(dateEnd)).toLocaleDateString();

      if (dateStart && dateEnd) {
        return (
          <>
            <DatesTagContainer
              filtersArea={filtersArea}
              filterGroupIndex={filterGroupIndex}
              label={`c ${handleStartDate}`}
              valueKey={CHF_DATE_START}
            />
            <DatesTagContainer
              filtersArea={filtersArea}
              filterGroupIndex={filterGroupIndex}
              label={`по ${handleEndDate}`}
              valueKey={CHF_DATE_END}
            />
          </>
        );
      } else if (dateStart && !dateEnd) {
        return (
          <DatesTagContainer
            filtersArea={filtersArea}
            filterGroupIndex={filterGroupIndex}
            label={`c ${handleStartDate}`}
            valueKey={CHF_DATE_START}
          />
        );
      } else if (!dateStart && dateEnd) {
        return (
          <DatesTagContainer
            filtersArea={filtersArea}
            filterGroupIndex={filterGroupIndex}
            label={`по ${handleEndDate}`}
            valueKey={CHF_DATE_END}
          />
        );
      }
    } else {
      return (
        <DatesTagContainer
          filtersArea={filtersArea}
          filterGroupIndex={filterGroupIndex}
          label={PERIODS.find(({ id }) => id === period)?.label}
          valueKey={CHF_DATE_START}
        />
      );
    }
  }, [filterGroupIndex, memoizedServerFilters]);

  const hasMinimumOneTag = useMemo(() => {
    const filter = serverFilters.VisitsFilter?.[filterGroupIndex]?.Values?.[0];
    const period = filter?.Period || null;
    const dateStart = filter?.DateStart || null;
    const dateEnd = filter?.DateEnd || null;

    if (period === null && (dateStart || dateEnd)) {
      return true;
    } else return period !== null && !dateStart && !dateEnd;
  }, [serverFilters, filterGroupIndex]);

  return (
    <Space
      className={`visits-period-label${filterGroupIndex}`}
      direction="vertical"
    >
      <span>Период</span>
      {hasMinimumOneTag && <>{periodTags}</>}
    </Space>
  );
};
